/**
 * MapFan認証API関連の関数
 */

import * as Sentry from "@sentry/react";

/** MapFan認証APIのエンドポイント */
const AUTH_ENDPOINT = import.meta.env.VITE_MAPFAN_AUTH_ENDPOINT || "";

/** MapFan認証APIキー */
const AUTH_API_KEY = import.meta.env.VITE_MAPFAN_AUTH_API_KEY || "";

/** 認証APIリクエストのタイムアウト (ms) */
const AUTH_TIMEOUT_MS = 5000;

/** キャッシュされたトークン */
let cachedToken: string | undefined;

/**
 * MapFan認証APIからトークンを取得する（取得済みの場合はキャッシュを返す）
 * @returns 認証トークン
 * @throws 認証に失敗した場合
 */
export const fetchMapFanToken = async (): Promise<string> => {
  if (cachedToken) return cachedToken;

  if (!AUTH_ENDPOINT) {
    const error = new Error("VITE_MAPFAN_AUTH_ENDPOINT が設定されていません");
    Sentry.captureException(error);
    throw error;
  }

  const headers: HeadersInit = AUTH_API_KEY ? { "x-api-key": AUTH_API_KEY } : {};
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), AUTH_TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(AUTH_ENDPOINT, { headers, signal: controller.signal });
    clearTimeout(timeoutId);
  } catch (error) {
    clearTimeout(timeoutId);
    Sentry.captureException(error);
    throw error;
  }

  if (!response.ok) {
    const error = new Error(`MapFan認証に失敗しました: ${response.status}`);
    Sentry.captureException(error);
    throw error;
  }

  const data: { key?: string } = await response.json();

  if (!data.key) {
    const error = new Error("MapFan認証レスポンスにトークンが含まれていません");
    Sentry.captureException(error);
    throw error;
  }

  cachedToken = data.key;
  return cachedToken;
};

/** キャッシュされたトークンを破棄する（期限切れ時に呼び出す） */
export const clearMapFanToken = (): void => {
  cachedToken = undefined;
};
